<template>
  <div>
    <div v-if="!question.isVoterEligible">
      <h1 class="headline">Skipping: {{ question.header }}</h1>
      <p>
        We're skipping this step because voting on this matter is limited to
        <b>{{ subgroupsFormatted }}.</b>
      </p>

      <p>Please click on the "Next" button to continue.</p>
      <div class="App-buttons">
        <question-button
          :is-disabled="false"
          @button-clicked="
            trackOption(
              'I abstain from voting and my proxy shall not vote on this matter'
            )
          "
        />
      </div>
    </div>
    <div v-else>
      <!-- ICON Depends on Election Type -->
      <div v-if="isTypeGeneral">
        <elections-general
          :question="question"
          :question-id="questionId"
          :has-candidates="hasCandidates"
          :votes="voteSelection"
          :nominees="question.nominees"
          :total-votes="numAvailableVotes"
          :votes-left="numVotesRemaining"
          :custom-options="question.meta.options"
          :has-duplicate-candidates-in-list="hasDuplicateCandidatesInList"
          :prevent-proxy-deferrals="preventProxyDeferrals"
          :has-election-ranking="hasElectionRanking"
          :type="question.type"
          @show-abstain-modal="openModal('abstain')"
          @show-option-modal="openModal('option')"
        />
      </div>

      <div v-else-if="isTypeOwnerOccupied">
        <elections-owner-occupied
          :question="question"
          :question-id="questionId"
          :has-candidates="hasCandidates"
          :votes="voteSelection"
          :nominees="question.nominees"
          :total-votes="numAvailableVotes"
          :votes-left="numVotesRemaining"
          :custom-options="question.meta.options"
          :has-duplicate-candidates-in-list="hasDuplicateCandidatesInList"
          :prevent-proxy-deferrals="preventProxyDeferrals"
          :has-election-ranking="hasElectionRanking"
          @show-abstain-modal="openModal('abstain')"
          @show-option-modal="openModal('option')"
        />
      </div>

      <div v-else>
        UNKNOWN ELECTION TYPE
      </div>
    </div>

    <!-- modal popup when votes unused -->
    <abstain-modal
      v-show="modals.abstain"
      :question-id="questionId"
      @close-modal="closeModal('abstain')"
      :total-votes="numAvailableVotes"
      :votes-left="numVotesRemaining"
      @accept-abstain-modal="acceptAbstainModal"
    />

    <!-- modal popup when ALL votes unused -->
    <option-modal
      v-show="modals.option"
      :question-id="questionId"
      :votes="abstainOption"
      :votes-left="numVotesRemaining"
      :prevent-proxy-deferrals="preventProxyDeferrals"
      :has-candidates="hasCandidates"
      :type="question.type"
      @close-modal="closeModal('option')"
    />
  </div>
</template>

<script>
import _ from 'lodash';

import ElectionsGeneral from './ElectionsGeneral';
import ElectionsOwnerOccupied from './ElectionsOwnerOccupied';
import AbstainModal from './Modals/AbstainModal';
import OptionModal from './Modals/OptionModal';
import QuestionButton from '../QuestionsButton.vue';
import {sanitizeCandidate, subgroups} from '@/vuex/helpers.js';
import {mapState, mapActions} from 'vuex';

export default {
  name: 'Elections',
  components: {
    ElectionsGeneral,
    ElectionsOwnerOccupied,
    AbstainModal,
    OptionModal,
    QuestionButton
  },
  props: {
    questionId: {
      type: [String, Number],
      required: true
    },
    question: {
      type: Object,
      required: true
    },
    meetingOptions: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  computed: {
    ...mapState('candidates', ['additionalVotesNum']),
    customOptions() {
      return this.question.meta.options;
    },
    hasCandidates() {
      return this.question.initialOptions.length !== 0;
    },
    hasDuplicateCandidatesInList() {
      const uniqueOptions = _.uniq(
        this.voteSelection.filter(Boolean).map(candidate => {
          return sanitizeCandidate(candidate);
        })
      );
      return uniqueOptions.length !== this.voteSelection.filter(Boolean).length;
    },
    hasElectionRanking() {
      if (
        this.meetingOptions.elections_ranking &&
        this.question.type !== 'simple-vote'
      ) {
        return true;
      }
      return false;
    },
    numAvailableVotes() {
      // For campaigns that have elections ranking turned on
      if (this.hasElectionRanking) {
        // When there are no candidates, the number of choices will be dependent on the number of positions
        const voteCount =
          this.question.nominees.length === 0
            ? this.question.positions
            : Math.max(this.question.nominees.length, this.question.positions);
        return Number(voteCount) + this.additionalVotesNum;
      } else {
        // Default
        return this.question.positions + this.numOptionalVotes;
      }
    },
    numOptionalVotes() {
      return _.has(this.customOptions, 'optionalVotes')
        ? parseInt(this.customOptions.optionalVotes)
        : 0;
    },
    numVotesRemaining() {
      return this.numAvailableVotes - this.voteSelection.length;
    },
    voteSelection() {
      return this.question.vote.selection.filter(Boolean);
    },
    abstainOption() {
      return this.question.vote.option.filter(Boolean);
    },
    isTypeOwnerOccupied() {
      return _.includes(
        ['owner-occupied', 'substitute-elections-oo'],
        this.question.type
      );
    },
    isTypeGeneral() {
      return _.includes(
        ['general-elections', 'substitute-elections', 'simple-vote'],
        this.question.type
      );
    },
    preventProxyDeferrals() {
      return _.get(this.meetingOptions, 'disable_proxy_deferrals', true);
    },
    subgroupsFormatted() {
      return subgroups(this.question);
    }
  },
  methods: {
    ...mapActions('questions', ['updateAbstainOption']),
    trackOption(option) {
      this.updateSelection(option);
    },
    updateSelection(option) {
      this.updateAbstainOption({
        questionId: this.questionId,
        option: [option]
      });
    },
    openModal(type) {
      switch (type) {
        case 'abstain':
          this.modals.abstain = true;
          break;
        case 'option':
          // If proxy holder is disabled, show the usual abstain modal
          if (_.get(this.meetingOptions, 'disable_proxy_holder', false)) {
            this.modals.abstain = true;
            break;
          }
          this.modals.option = true;
          break;
        default:
      }
    },
    closeModal(type) {
      switch (type) {
        case 'abstain':
          this.modals.abstain = false;
          break;
        case 'option':
          this.modals.option = false;
          break;
        default:
      }
    },
    acceptAbstainModal() {
      this.goNextQuestion({hasAbstain: true});
    }
  },
  data() {
    return {
      modals: {
        abstain: false,
        option: false
      },
      path: this.$route.path.split('?')[0]
    };
  },
  mounted() {
    this.$nextTick(() => {
      componentHandler.upgradeAllRegistered();
    });
  }
};
</script>

<style lang="scss" scoped></style>
